import { useMemo, useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/shared/ui/Modal';
import { Button } from '@/shared/ui/Button';
import { Select } from '@/shared/ui/Select';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/Toast';
import { agencyApi, type Location, type PromptDescriptor } from '../api/agencyApi';
import { agencyKeys } from '../queryKeys';
import styles from './ModelOverridesModal.module.css';

interface ModelOverridesModalProps {
  isOpen: boolean;
  onClose: () => void;
  practiceId: string;
  location: Location;
}

const DEFAULT_VALUE = '__default__';

export function ModelOverridesModal({
  isOpen,
  onClose,
  practiceId,
  location,
}: ModelOverridesModalProps) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: registryResp, isLoading } = useQuery({
    queryKey: ['agency', 'prompt-registry'],
    queryFn: () => agencyApi.getPromptRegistry(),
    enabled: isOpen,
    staleTime: 5 * 60_000,
  });
  const registry = registryResp?.data;

  const [overrides, setOverrides] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) setOverrides({ ...(location.anthropicModels ?? {}) });
  }, [isOpen, location.anthropicModels]);

  const groups = useMemo(() => {
    if (!registry) return [];
    const byGroup = new Map<string, PromptDescriptor[]>();
    for (const prompt of registry.prompts) {
      const existing = byGroup.get(prompt.group) ?? [];
      existing.push(prompt);
      byGroup.set(prompt.group, existing);
    }
    return Array.from(byGroup.entries()).map(([group, prompts]) => ({ group, prompts }));
  }, [registry]);

  const mutation = useMutation({
    mutationFn: (next: Record<string, string> | null) =>
      agencyApi.updateLocation(practiceId, location.id, { anthropicModels: next }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.practice(practiceId) });
      toast.success('Model overrides saved.');
      onClose();
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to save overrides.';
      toast.error(message);
    },
  });

  const handleChange = (promptName: string, value: string) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (value === DEFAULT_VALUE) delete next[promptName];
      else next[promptName] = value;
      return next;
    });
  };

  const handleSave = () => {
    const next = Object.keys(overrides).length > 0 ? overrides : null;
    mutation.mutate(next);
  };

  const handleReset = () => {
    setOverrides({});
  };

  const defaultLabel = registry
    ? (registry.models.find((m) => m.id === registry.defaultModel)?.label ?? registry.defaultModel)
    : '';

  const overrideCount = Object.keys(overrides).length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Model overrides — ${location.name}`}
      size="lg"
      footer={
        <div className={styles.footer}>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            disabled={overrideCount === 0 || mutation.isPending}
          >
            Reset all to default
          </Button>
          <div className={styles.actions}>
            <Button variant="ghost" size="sm" onClick={onClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} loading={mutation.isPending}>
              Save
            </Button>
          </div>
        </div>
      }
    >
      {isLoading || !registry ? (
        <div className={styles.loadingState}>
          <Spinner />
        </div>
      ) : (
        <>
          <p className={styles.intro}>
            Pick a model for each AI task on this location. Tasks left on <strong>Default</strong>{' '}
            use the account default ({defaultLabel}). Agent skills can only use Claude models.
            {registry.openRouterEnabled
              ? ''
              : ' OpenRouter is off right now, so tasks set to an OpenRouter model run on their default.'}
          </p>
          <div className={styles.groups}>
            {groups.map(({ group, prompts }) => (
              <div key={group} className={styles.group}>
                <h4 className={styles.groupTitle}>{group}</h4>
                {prompts.map((prompt) => {
                  const current = overrides[prompt.name] ?? DEFAULT_VALUE;
                  return (
                    <div key={prompt.name} className={styles.row}>
                      <div>
                        <div className={styles.rowLabel}>{prompt.label}</div>
                        <div className={styles.rowHint}>{prompt.name}</div>
                      </div>
                      <Select
                        value={current}
                        onChange={(e) => handleChange(prompt.name, e.target.value)}
                        options={[
                          { value: DEFAULT_VALUE, label: `Default (${defaultLabel})` },
                          ...registry.models
                            .filter((m) => !prompt.claudeOnly || m.provider === 'anthropic')
                            .map((m) => ({ value: m.id, label: m.label })),
                        ]}
                        fullWidth
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}
